import { Clock3 } from "lucide-react";
import { Button } from "src/components/buttons/shadcn-button";
import { Form, FormControl, FormField, FormItem, FormMessage } from "src/components/form/form";
import { Input } from "src/components/input/input";
import { EditDietFormValues, EditDietSchema } from "../schema/dietary-report.schema";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "react-toastify";
import { useEditDiet } from "../hooks/useEditDiet";
import { DietReport } from "src/redux/types/_customer.type";

const EditDietForm = ({ report, memberId, close }: { report: DietReport, memberId: number, close: () => void }) => {
  const form = useForm<EditDietFormValues>({
    resolver: zodResolver(EditDietSchema),
    defaultValues: report,
  });

  const { mutate: editDiet, isPending } = useEditDiet();

  const onSubmit = async (data: EditDietFormValues) => {
    if (!report.id) {
      toast.error("Select Report");
      return;
    }

    editDiet(
      {
        values: data,
        reportId: report.id,
        memberId
      },
      {
        onSuccess: () => {
          close();
        },
      },
    );
  };

  return (
    <div>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
          {/* Day */}
          <div className="rounded-xl shadow-sm border border-gray-200 p-3 hover:shadow-md transition flex gap-12 items-center">
            <h3 className="font-medium px-6 py-2 bg-gradient-to-r from-blue-100 to-blue-50 rounded-xl text-[#42679e]">
              Day
            </h3>
            <FormField
              control={form.control}
              name="day"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <select
                      {...field}
                      value={field.value ?? ""}
                      onChange={(e) => field.onChange(e.target.value)}
                      className="border border-gray-300 rounded-xl px-3 py-3 text-[#42679e] font-medium focus:border-blue-500"
                    >
                      <option value="">Select Day</option>
                      {["5", "4", "3", "2", "1"].map((d) => (
                        <option key={d} value={d}>
                          {d}
                        </option>
                      ))}
                    </select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="grid grid-cols-2 gap-4 space-y-4">
            {[
              {
                title: "Morning Breakfast",
                timeName: "morning_meal_fast_time",
                mealName: "morning_meal_fast",
              },
              {
                title: "Lunch",
                timeName: "lunch_time",
                mealName: "lunch",
              },
              {
                title: "Evening snacks",
                timeName: "evening_snacks_time",
                mealName: "evening_snacks",
              },
              {
                title: "Dinner",
                timeName: "dinner_time",
                mealName: "dinner",
              },
              {
                title: "Anything In Between",
                timeName: "anything_between_time",
                mealName: "anything_between",
              },
            ].map((section, i) => (
              <div
                key={i}
                className="rounded-xl shadow-sm border border-gray-200 p-5 hover:shadow-md transition"
              >
                <h3 className="font-medium mb-3 border-b pb-1 bg-gradient-to-r from-blue-100 to-blue-50 rounded-xl p-2 text-[#42679e]">
                  {section.title}
                </h3>
                <div className="grid grid-cols-2 gap-5 items-center">
                  {/* Time field with clock icon */}
                  <div className="relative">
                    <Clock3 className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                    <FormField
                      control={form.control}
                      name={section.timeName as keyof EditDietFormValues}
                      render={({ field }) => (
                        <FormItem>
                          <FormControl>
                            <Input
                              type="time"
                              className="pl-10 border-gray-300 focus:border-blue-500 focus:ring-blue-500 text-[#42679e] font-medium"
                              {...field}
                              value={field.value ?? ""}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  {/* Meal description field */}
                  <FormField
                    control={form.control}
                    name={section.mealName as keyof EditDietFormValues}
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <Input
                            placeholder="Enter meal details"
                            className="border-gray-300 focus:border-blue-500 focus:ring-blue-500 text-[#42679e] font-medium"
                            {...field}
                            value={field.value ?? ""}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>
            ))}
          </div>

          {/* Submit Button */}
          <div className="flex justify-center">
            <Button type="submit" disabled={isPending}>
              {isPending ? (
                <span className="loading loading-dots loading-md"/>
              ) : (
                "Save Report"
              )}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
};

export default EditDietForm;
